import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { EditorProvider } from "../../state/editor/useEditor";
import { CapsProvider } from "./CapsProvider";
import { TopBar } from "./TopBar";
import { EditorChrome } from "./EditorChrome";
import { CARD_GAP } from "./layout";
import { StudioModelContext } from "./studioModel";
import type { ModelLoadStatus } from "./ModelLoadOverlay";

/**
 * The studio shell — the only one. The iframe lives in `main` and is mounted
 * regardless of ready-state so the handshake can complete; the chrome (rails /
 * panels / toolbar) overlays it once ready, inside the Caps + Editor providers,
 * and `main` is padded by however much room that chrome takes.
 *
 * There used to be a second chrome here (`CanvasChrome`, a flat-slate re-skin)
 * selected by a `chrome` prop and a floating V1/V2 pill. It is gone; everything
 * it could do that this one could not was ported first.
 */
export function EditorShell({
  iframe,
  ready,
  onFullscreen,
  viewerOrigin,
  modelLoad,
  onRetryModel,
  topBarActions,
  topBarLeading,
  modelId,
  modelName,
  overlay,
}: {
  iframe: ReactNode;
  ready: boolean;
  onFullscreen: () => void;
  viewerOrigin: string;
  /** Studio model-load progress (see ModelLoadOverlay). */
  modelLoad?: ModelLoadStatus;
  onRetryModel?: () => void;
  /** Host-owned actions for the top bar's right-hand side (model switcher,
   *  Save, Configurator…). The bar itself knows nothing about the Vault. */
  topBarActions?: ReactNode;
  /** Host content right after the wordmark (the Vault back link). */
  topBarLeading?: ReactNode;
  /** The Vault model's own name, shown in the top bar over the file name. */
  modelName?: string;
  /** The Vault model this studio is open on, if any. Undefined on /dev/studio. */
  modelId?: string;
  /** Host drawers that need the editor's providers (the configurator drawer
   *  draws the material library). Rendered above the chrome once ready. */
  overlay?: ReactNode;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  // The chrome no longer resizes the canvas, so the shell does not track its
  // width any more — `EditorChrome` positions the floating toolbar from its own
  // copy. `containerW` is still fed in because the panels size themselves
  // against the window.
  const [rowW, setRowW] = useState(0);

  // The chrome still reports its width — the floating toolbar positions itself
  // clear of the right panel — but that width no longer resizes the canvas.
  // Panels overlay it.
  useEffect(() => {
    const el = rowRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([e]) => setRowW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pad = ready;
  return (
    <div className="editor-theme flex h-full flex-col bg-ed-bg text-ed-text">
      <TopBar leading={topBarLeading} actions={topBarActions} />
      <div ref={rowRef} className="relative flex min-h-0 flex-1">
        {/* ⚠️ `main` must keep its exact position in the element tree across the
            `ready` flip. ViewerHost creates the ViewerClient in an effect keyed
            on the iframe src, capturing `iframeRef.current` — if React ever
            re-parents this node the iframe is destroyed and recreated, the
            effect does not re-run, and the client posts into a dead
            contentWindow. RPC has no timeout, so every call would hang forever
            with nothing on screen to say why. So this node never moves and its
            box never depends on which panels are open.

            The padding is now a CONSTANT gap, not the chrome insets. The panels
            float over the canvas instead of squeezing it: the canvas keeps the
            full width at all times, and a panel opening no longer makes the
            viewer reallocate its drawing buffer. That also removes the reason
            the narrow-window clamp existed — there is no width left to protect
            because the canvas never gives any up. */}
        <main
          className="relative min-w-0 flex-1 bg-ed-bg"
          style={{
            paddingLeft: pad ? CARD_GAP : 0,
            paddingRight: pad ? CARD_GAP : 0,
            paddingTop: pad ? CARD_GAP : 0,
            paddingBottom: pad ? CARD_GAP : 0,
          }}
        >
          {/* The canvas card. Checkerboard rides HERE rather than on `main`:
              the viewer canvas is alpha-enabled and `Set Transparent BG` really
              does clear to alpha 0 — but the iframe used to sit on a flat
              `bg-ed-bg`, so "transparent" looked identical to having picked that
              colour and the feature read as broken. An opaque viewer background
              covers this completely, so it only shows where the render is
              genuinely transparent.
              `isolation` is not decorative: WebKit will not clip an iframe to a
              rounded `overflow-hidden` parent without its own stacking context,
              and the iframe carries the same radius for the same reason. */}
          <div
            className={`relative h-full w-full overflow-hidden ${
              pad ? "rounded-xl border border-ed-border shadow-[0_1px_3px_rgba(0,0,0,0.06)]" : ""
            }`}
            style={{
              isolation: "isolate",
              backgroundImage:
                "repeating-conic-gradient(rgba(128,128,128,0.18) 0% 25%, transparent 0% 50%)",
              backgroundSize: "20px 20px",
            }}
          >
            {iframe}
          </div>
          <AnimatePresence>
            {!ready ? (
              <motion.div
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="absolute inset-0 z-20 flex items-center justify-center bg-ed-bg"
              >
                <motion.span
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                  className="text-[12px] font-semibold text-ed-text-3"
                >
                  Loading
                </motion.span>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </main>
        {ready ? (
          <CapsProvider>
            <EditorProvider>
              <StudioModelContext.Provider value={modelId}>
              <EditorChrome
                onFullscreen={onFullscreen}
                viewerOrigin={viewerOrigin}
                modelLoad={modelLoad}
                onRetryModel={onRetryModel}
                containerW={rowW}
                modelName={modelName}
                />
                {overlay}
              </StudioModelContext.Provider>
            </EditorProvider>
          </CapsProvider>
        ) : null}
      </div>
    </div>
  );
}
